import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { CloseOutlined, OpenInFull } from "@mui/icons-material";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Map, Marker, useMapsLibrary } from "@vis.gl/react-google-maps";
import { setCoordinates } from "../features/assessorSlice";
import { useMapsKey } from "./mapsProvider";

const isUsableAddress = (address) =>
  Boolean(address && address.trim() && address.trim() !== "NOT FOUND");

// Small map of the property's location, expandable to a dialog. Uses stored
// latitude/longitude when present; otherwise geocodes the building address
// once and stores the result in the form (persisted on the next save).
const PropertyMapInner = () => {
  const dispatch = useDispatch();
  const geocoding = useMapsLibrary("geocoding");
  const latitude = useSelector((state) => state.assessor.value.form.latitude);
  const longitude = useSelector((state) => state.assessor.value.form.longitude);
  const buildingAddress = useSelector(
    (state) => state.assessor.value.form.buildingAddress
  );
  const [expanded, setExpanded] = useState(false);
  const lastGeocoded = useRef(null);

  const hasCoords = Number.isFinite(latitude) && Number.isFinite(longitude);

  useEffect(() => {
    if (!geocoding || hasCoords || !isUsableAddress(buildingAddress)) {
      return;
    }
    if (lastGeocoded.current === buildingAddress) {
      return; // already tried this address — don't burn quota retrying
    }

    // Debounced so typing an address by hand doesn't geocode every keystroke.
    const timer = setTimeout(() => {
      lastGeocoded.current = buildingAddress;
      const geocoder = new geocoding.Geocoder();
      geocoder
        .geocode({ address: buildingAddress })
        .then((response) => {
          const location = response?.results?.[0]?.geometry?.location;
          if (location) {
            dispatch(
              setCoordinates({ latitude: location.lat(), longitude: location.lng() })
            );
          }
        })
        .catch(() => {
          // Address didn't geocode — leave the map hidden.
        });
    }, 1200);
    return () => clearTimeout(timer);
  }, [geocoding, hasCoords, buildingAddress, dispatch]);

  if (!hasCoords) {
    return null;
  }

  const position = { lat: latitude, lng: longitude };
  // Key remounts the map when the property moves, so defaultCenter re-applies
  // while leaving pan/zoom free in between (a controlled `center` would snap
  // back on every render).
  const mapKey = `${latitude},${longitude}`;
  const mapProps = {
    defaultCenter: position,
    defaultZoom: 17,
    gestureHandling: "cooperative",
    disableDefaultUI: false,
  };

  return (
    <Box sx={{ position: "relative", width: "100%" }}>
      <Box sx={{ height: 220, width: "100%", borderRadius: 1, overflow: "hidden" }}>
        <Map key={mapKey} {...mapProps}>
          <Marker position={position} />
        </Map>
      </Box>
      <Tooltip title="Expand map">
        <IconButton
          size="small"
          onClick={() => setExpanded(true)}
          sx={{
            position: "absolute",
            top: 6,
            right: 6,
            backgroundColor: "white",
            "&:hover": { backgroundColor: "#eee" },
          }}
        >
          <OpenInFull fontSize="small" />
        </IconButton>
      </Tooltip>

      <Dialog
        fullWidth
        maxWidth="lg"
        open={expanded}
        onClose={() => setExpanded(false)}
      >
        <DialogTitle
          sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
        >
          <Typography component="span" variant="h6">
            {buildingAddress || "Property Location"}
          </Typography>
          <IconButton onClick={() => setExpanded(false)}>
            <CloseOutlined />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ height: "70vh", p: 0 }}>
          <Map key={`dialog-${mapKey}`} {...mapProps}>
            <Marker position={position} />
          </Map>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

const PropertyMap = () => {
  const mapsKey = useMapsKey();
  if (!mapsKey) {
    return null;
  }
  return <PropertyMapInner />;
};

export default PropertyMap;
